import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Loader2, Download, ArrowUpRight, Database, AlertCircle, Users, List } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const KIND_LABELS = {
  total: "All conversions",
  call: "Call-credited",
  sms: "SMS-credited",
  wn: "Wrong-number (deposited)",
  invalid: "Invalid-number (deposited)",
};

const CREDIT_BADGE_STYLES = {
  yes: "bg-emerald-100 text-emerald-800 border-emerald-200",
  sms: "bg-blue-100 text-blue-800 border-blue-200",
  unsure: "bg-amber-100 text-amber-800 border-amber-200",
  no: "bg-slate-100 text-slate-600 border-slate-200",
};

// Human-readable credit kind label for the pill.
function creditLabel(row) {
  const s = row.credit_status;
  if (s === "yes") return "Call";
  if (s === "sms") return "SMS";
  if (s === "unsure") {
    const d = (row.disposition_lower || "").toLowerCase();
    if (d === "wrong number") return "WN";
    if (d === "invalid number") return "Invalid";
    return "Unsure";
  }
  return s || "—";
}

function fmtDate(iso) {
  if (!iso) return "—";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return iso;
  return t.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function fmtDateOnly(iso) {
  if (!iso) return "—";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return iso;
  return t.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtAmt(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

/**
 * Slide-out drawer that shows the actual rows behind a converted-count cell.
 *
 * Open by setting `state.open = true` from anywhere in Ops Dashboard and passing
 * the current filters (kind, agent_id, brand_id, date_from, date_to, team_id,
 * campaign_id). The drawer fetches /api/ops/analytics/converted-users and
 * renders a scrollable table with a credit-reason column.
 */
export default function ConvertedUsersDrilldown({ state, onClose }) {
  const { user } = useAuth();
  // Business rule: NEVER show currency/amount fields to ops-team users.
  // The backend enforces this too via /converted-users hide_financials, but
  // we also gate the UI so the columns simply don't render.
  const canSeeMoney = !!(user?.is_admin || user?.is_super_admin);
  const [data, setData] = useState(null);
  // Live-verify state keyed by row index: { loading, error, data }
  const [verifyState, setVerifyState] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // View mode: "rows" = one row per credited call attempt (raw);
  //            "users" = one row per unique (brand_id, customer) — matches KPI card.
  const [viewMode, setViewMode] = useState("rows");

  const open = !!state?.open;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setViewMode("rows"); // reset each time the drawer opens for a new scope
    (async () => {
      setLoading(true); setErr(""); setData(null);
      try {
        const r = await api.get("/ops/analytics/converted-users", {
          params: {
            kind: state?.kind || "total",
            brand_id: state?.brand_id ?? undefined,
            date_from: state?.date_from ?? undefined,
            date_to: state?.date_to ?? undefined,
            team_id: state?.team_id ?? undefined,
            campaign_id: state?.campaign_id ?? undefined,
            agent_id: state?.agent_id ?? undefined,
            limit: 5000,
          },
        });
        if (!cancelled) setData(r.data);
      } catch (ex) {
        if (!cancelled) setErr(ex?.response?.data?.detail || "Failed to load drill-down.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, state?.kind, state?.brand_id, state?.date_from, state?.date_to,
      state?.team_id, state?.campaign_id, state?.agent_id]);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      params.set("kind", state?.kind || "total");
      if (state?.brand_id) params.set("brand_id", String(state.brand_id));
      if (state?.date_from) params.set("date_from", state.date_from);
      if (state?.date_to) params.set("date_to", state.date_to);
      if (state?.team_id) params.set("team_id", state.team_id);
      if (state?.campaign_id) params.set("campaign_id", state.campaign_id);
      if (state?.agent_id) params.set("agent_id", state.agent_id);
      const r = await api.get(`/ops/analytics/converted-users.csv?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `converted_users_${state?.kind || "total"}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "CSV export failed");
    }
  };

  // Admin-only: live-query MySQL payment_txns for a row and cache the
  // result. Runs sequentially per row (each call opens an SSH tunnel).
  const runVerify = async (i, row) => {
    setVerifyState((s) => ({ ...s, [i]: { loading: true } }));
    try {
      const { data } = await api.get("/ops/analytics/verify-mysql-deposits", {
        params: {
          brand_id: row.brand_id,
          customer_name: row.customer_name,
          around_date: (row.attempt_date_ist || "").slice(0, 10),
          window_days: 90,
        },
      });
      setVerifyState((s) => ({ ...s, [i]: { loading: false, data } }));
    } catch (ex) {
      setVerifyState((s) => ({ ...s, [i]: {
        loading: false,
        error: ex?.response?.data?.detail || "Verify failed",
      }}));
    }
  };

  const kindLabel = KIND_LABELS[state?.kind] || "Conversions";
  const rows = data?.rows || [];
  const totalCount = data?.total ?? 0;
  const truncated = rows.length < totalCount;
  // Server-authoritative unique-user count (deduped by brand_id +
  // customer_name_lower to match the KPI card's hybrid bucketing).
  // Falls back to a client-side dedup over the loaded rows if an older
  // backend is deployed that doesn't return unique_users yet.
  const serverUniqueUsers = data?.unique_users;
  const uniqueUsers = useMemo(() => {
    if (typeof serverUniqueUsers === "number") return serverUniqueUsers;
    const seen = new Set();
    for (const r of rows) {
      const key = `${r.brand_id || ""}::${(r.customer_name || "").toLowerCase()}`;
      seen.add(key);
    }
    return seen.size;
  }, [rows, serverUniqueUsers]);
  // Only show the "(from loaded sample)" hint when we don't have the
  // server-authoritative number.
  const uniqueUsersIsSample = typeof serverUniqueUsers !== "number" && truncated;

  // Grouped view: one entry per unique (brand_id, customer). Aggregates
  // every credited call-attempt for the user so ops can see whether the
  // multiple rows in the "raw" view are the same user being dialled by
  // different agents (valid multi-agent activity) or an ingestion issue.
  const CREDIT_RANK = { yes: 3, sms: 2, unsure: 1, no: 0 };
  const groupedRows = useMemo(() => {
    const byKey = new Map();
    for (const r of rows) {
      const key = `${r.brand_id || ""}::${(r.customer_name || "").toLowerCase()}`;
      const g = byKey.get(key);
      if (!g) {
        byKey.set(key, {
          key,
          customer_name: r.customer_name,
          phone: r.phone,
          brand_id: r.brand_id,
          brand_name: r.brand_name,
          reactivated_flag: r.reactivated_flag,
          attempts: 1,
          agents: new Set([r.agent_name].filter(Boolean)),
          campaigns: new Set([r.campaign_name].filter(Boolean)),
          dispositions: new Set([r.disposition_name].filter(Boolean)),
          first_attempt: r.attempt_datetime,
          last_attempt: r.attempt_datetime,
          best_credit_status: r.credit_status,
          best_row: r,
          deposit_count_after: r.deposit_count_after ?? 0,
          deposit_sum_after: Number(r.deposit_sum_after || 0),
        });
      } else {
        g.attempts += 1;
        if (r.agent_name) g.agents.add(r.agent_name);
        if (r.campaign_name) g.campaigns.add(r.campaign_name);
        if (r.disposition_name) g.dispositions.add(r.disposition_name);
        if (r.attempt_datetime && (!g.first_attempt || r.attempt_datetime < g.first_attempt)) {
          g.first_attempt = r.attempt_datetime;
        }
        if (r.attempt_datetime && (!g.last_attempt || r.attempt_datetime > g.last_attempt)) {
          g.last_attempt = r.attempt_datetime;
        }
        // Use the highest-quality credit reason as the group's representative.
        if ((CREDIT_RANK[r.credit_status] ?? -1) > (CREDIT_RANK[g.best_credit_status] ?? -1)) {
          g.best_credit_status = r.credit_status;
          g.best_row = r;
        }
        // Deposit counts should be per-user, not summed across rows —
        // take the max across attempts since MongoDB writes the same
        // enriched value on every attempt for the same user.
        g.deposit_count_after = Math.max(g.deposit_count_after, r.deposit_count_after ?? 0);
        g.deposit_sum_after = Math.max(g.deposit_sum_after, Number(r.deposit_sum_after || 0));
      }
    }
    // Sort by attempts desc, then most recent last_attempt desc.
    return Array.from(byKey.values()).sort((a, b) => {
      if (b.attempts !== a.attempts) return b.attempts - a.attempts;
      return String(b.last_attempt || "").localeCompare(String(a.last_attempt || ""));
    });
  }, [rows]);

  const scopeSummary = useMemo(() => {
    const bits = [];
    if (state?.date_from || state?.date_to) {
      bits.push(`${state?.date_from || "…"} → ${state?.date_to || "…"}`);
    }
    if (state?.agent_name) bits.push(`Agent: ${state.agent_name}`);
    if (state?.campaign_name) bits.push(`Campaign: ${state.campaign_name}`);
    if (state?.brand_name) bits.push(`Brand: ${state.brand_name}`);
    if (state?.team_name) bits.push(`Team: ${state.team_name}`);
    return bits.join(" · ") || "All available data";
  }, [state]);

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <SheetContent side="right" className="w-full sm:max-w-4xl overflow-hidden flex flex-col p-0"
        data-testid="converted-users-drawer">
        <SheetHeader className="p-4 pr-12 border-b border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-base">
                {kindLabel} · {totalCount.toLocaleString()} credited row{totalCount === 1 ? "" : "s"}
                {rows.length > 0 && (
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    · {uniqueUsers.toLocaleString()} unique user{uniqueUsers === 1 ? "" : "s"}
                    {uniqueUsersIsSample && " (from loaded sample)"}
                  </span>
                )}
              </SheetTitle>
              <SheetDescription className="text-xs text-slate-500 truncate" title="A single user can be credited on multiple call rows if they were dialled by different agents in the window; the KPI card deduplicates to unique users, while this drawer lists every credited row.">
                {scopeSummary}
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0 mr-4">
              {rows.length > 0 && (
                <div
                  className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[11px]"
                  data-testid="converted-users-viewmode"
                  role="tablist"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === "rows"}
                    onClick={() => setViewMode("rows")}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded ${
                      viewMode === "rows"
                        ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid="converted-users-viewmode-rows"
                    title="Show one row per credited call attempt (raw). Useful to see individual agent dial history."
                  >
                    <List className="w-3 h-3" /> Rows
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === "users"}
                    onClick={() => setViewMode("users")}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded ${
                      viewMode === "users"
                        ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid="converted-users-viewmode-users"
                    title="Group by unique user. Count matches the KPI card that opened this drawer."
                  >
                    <Users className="w-3 h-3" /> By user
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={downloadCsv}
                className="inline-flex items-center gap-1 text-xs text-[#0A84FF] hover:underline"
                data-testid="converted-users-csv"
                disabled={loading || rows.length === 0}
              >
                <Download className="w-3 h-3" /> Export CSV
              </button>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-auto" data-testid="converted-users-body">
          {loading && (
            <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {err && (
            <div className="p-6 text-sm text-red-600" data-testid="converted-users-error">{err}</div>
          )}
          {!loading && !err && rows.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              No credited conversions match this filter.
            </div>
          )}
          {!loading && !err && rows.length > 0 && viewMode === "rows" && (
            <table className="w-full text-xs" data-testid="converted-users-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Agent</th>
                  <th className="text-left px-3 py-2">Campaign</th>
                  <th className="text-left px-3 py-2">Called at</th>
                  <th className="text-left px-3 py-2">Credit</th>
                  {canSeeMoney && (
                    <th className="text-right px-3 py-2" title="Amount deposited on the same day as the call. Note: a credit can still be given if the user deposits later within the campaign window — see 'deposit trail + rule'.">
                      Deposit on day
                    </th>
                  )}
                  <th className="text-left px-3 py-2 w-80">Why credited</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={`${r.customer_name}-${r.attempt_datetime}-${i}`}
                      className="hover:bg-slate-50" data-testid={`converted-row-${i}`}>
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-slate-900">{r.customer_name || "—"}</div>
                      {r.phone && <div className="text-[10px] text-slate-500">{r.phone}</div>}
                      {r.reactivated_flag && (
                        <span className="mt-0.5 inline-block text-[9px] px-1 py-0 rounded-full bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200">
                          Reactivated
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div>{r.agent_name || "—"}</div>
                      {r.brand_name && <div className="text-[10px] text-slate-500">{r.brand_name}</div>}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div>{r.campaign_name || "—"}</div>
                      <div className="text-[10px] text-slate-500">
                        {r.disposition_name || "—"}
                        {r.is_followup ? " · follow-up" : r.is_fresh ? " · fresh" : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top whitespace-nowrap">
                      {fmtDate(r.attempt_datetime)}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${
                        CREDIT_BADGE_STYLES[r.credit_status] || CREDIT_BADGE_STYLES.no
                      }`}>
                        {creditLabel(r)}
                      </span>
                    </td>
                    {canSeeMoney && (
                      <td className="px-3 py-2 align-top text-right font-mono">
                        {fmtAmt(r.deposit_on_day_amount)}
                        {r.deposit_on_day_count > 0 && (
                          <div className="text-[10px] text-slate-500">
                            {r.deposit_on_day_count} deposit{r.deposit_on_day_count === 1 ? "" : "s"} on day
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 align-top text-slate-700">
                      <div>{r.credit_reason || "—"}</div>
                      <details className="mt-1">
                        <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600 select-none">
                          <ArrowUpRight className="w-2.5 h-2.5 inline align-baseline" /> deposit trail + rule
                        </summary>
                        <div className="mt-1 text-[10px] text-slate-500 space-y-0.5 font-mono">
                          <div>Registered: {fmtDateOnly(r.registration_date)}</div>
                          <div>Inactive (today): {r.inactive_days_today ?? "—"}d · (before call): {r.days_since_last_deposit_before ?? "—"}d</div>
                          {canSeeMoney ? (
                            <>
                              <div>Deposits before: {r.deposit_count_before ?? 0} (sum {fmtAmt(r.deposit_sum_before)}) · last {fmtDateOnly(r.last_deposit_date_before)}</div>
                              <div>Deposits after:  {r.deposit_count_after ?? 0} (sum {fmtAmt(r.deposit_sum_after)}) · last {fmtDateOnly(r.last_deposit_date_after)}</div>
                            </>
                          ) : (
                            <>
                              <div>Deposits before: {r.deposit_count_before ?? 0} · last {fmtDateOnly(r.last_deposit_date_before)}</div>
                              <div>Deposits after:  {r.deposit_count_after ?? 0} · last {fmtDateOnly(r.last_deposit_date_after)}</div>
                            </>
                          )}
                          <div>Rule: cooldown {r.campaign_rule_min_inactive_days ?? 0}d · window {r.campaign_rule_allowed_period_after_call ?? 0}d</div>
                          {canSeeMoney && (
                            <div className="mt-2 pt-2 border-t border-slate-100">
                              {!verifyState[i] && (
                                <button
                                  type="button"
                                  onClick={() => runVerify(i, r)}
                                  className="inline-flex items-center gap-1 text-[10px] font-medium text-[#0A84FF] hover:underline"
                                  data-testid={`verify-mysql-btn-${i}`}
                                  title="Cross-check this user's actual deposits from prod MySQL"
                                >
                                  <Database className="w-3 h-3" /> Verify against MySQL
                                </button>
                              )}
                              {verifyState[i]?.loading && (
                                <div className="flex items-center gap-2 text-[10px] text-slate-500">
                                  <Loader2 className="w-3 h-3 animate-spin" /> Live-querying payment.payment_txns…
                                </div>
                              )}
                              {verifyState[i]?.error && (
                                <div className="flex items-start gap-1 text-[10px] text-red-600" data-testid={`verify-mysql-error-${i}`}>
                                  <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                                  {verifyState[i].error}
                                </div>
                              )}
                              {verifyState[i]?.data && (
                                <div className="text-[10px] space-y-1" data-testid={`verify-mysql-result-${i}`}>
                                  <div className="font-semibold text-slate-700 flex items-center gap-1">
                                    <Database className="w-3 h-3 text-emerald-600" />
                                    MySQL says: {verifyState[i].data.deposits_in_window.count} deposits (sum {fmtAmt(verifyState[i].data.deposits_in_window.sum_amount)})
                                    <span className="text-slate-400 font-normal">
                                      in ±{verifyState[i].data.window.days}d window
                                    </span>
                                  </div>
                                  {verifyState[i].data.rows.length === 0 ? (
                                    <div className="text-slate-500 italic">No transactions found in MySQL for this user + brand.</div>
                                  ) : (
                                    <div className="max-h-40 overflow-y-auto border border-slate-200 rounded">
                                      <table className="w-full text-[10px]">
                                        <thead className="bg-slate-50 sticky top-0">
                                          <tr>
                                            <th className="text-left px-1.5 py-0.5">When</th>
                                            <th className="text-left px-1.5 py-0.5">Type</th>
                                            <th className="text-right px-1.5 py-0.5">Amount</th>
                                            <th className="text-left px-1.5 py-0.5">Status</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {verifyState[i].data.rows.slice(0, 50).map((row) => (
                                            <tr key={row.id} className="border-t border-slate-100">
                                              <td className="px-1.5 py-0.5">{fmtDate(row.created)}</td>
                                              <td className="px-1.5 py-0.5 font-mono">{row.payment_type}</td>
                                              <td className={`px-1.5 py-0.5 text-right font-mono ${(row.payment_type || "").toUpperCase() === "DEPOSIT" ? "text-emerald-700" : "text-slate-500"}`}>
                                                {fmtAmt(row.amount)}
                                              </td>
                                              <td className={`px-1.5 py-0.5 ${(row.status || "").toUpperCase() === "PROCESSED" ? "text-emerald-600" : "text-amber-600"}`}>{row.status}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && !err && rows.length > 0 && viewMode === "users" && (
            <table className="w-full text-xs" data-testid="converted-users-grouped-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Brand</th>
                  <th className="text-right px-3 py-2" title="Number of credited call attempts for this user in the current window.">Attempts</th>
                  <th className="text-left px-3 py-2">Agents</th>
                  <th className="text-left px-3 py-2">Campaign(s)</th>
                  <th className="text-left px-3 py-2">First → Last attempt</th>
                  <th className="text-left px-3 py-2">Best credit</th>
                  {canSeeMoney && (
                    <th className="text-right px-3 py-2" title="Deposits by this user after their first credited call (in campaign window).">
                      Deposits after
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groupedRows.map((g, i) => {
                  const agentList = Array.from(g.agents);
                  const campaignList = Array.from(g.campaigns);
                  return (
                    <tr key={g.key} className="hover:bg-slate-50" data-testid={`converted-user-row-${i}`}>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-slate-900">{g.customer_name || "—"}</div>
                        {g.phone && <div className="text-[10px] text-slate-500">{g.phone}</div>}
                        {g.reactivated_flag && (
                          <span className="mt-0.5 inline-block text-[9px] px-1 py-0 rounded-full bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200">
                            Reactivated
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">{g.brand_name || g.brand_id || "—"}</td>
                      <td className="px-3 py-2 align-top text-right font-mono">
                        <span
                          className={`inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                            g.attempts > 1
                              ? "bg-amber-50 text-amber-800 border border-amber-200"
                              : "bg-slate-50 text-slate-600 border border-slate-200"
                          }`}
                          title={g.attempts > 1 ? "This user was credited on multiple call rows." : "Single credited call."}
                        >
                          {g.attempts}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="text-slate-700">
                          {agentList.slice(0, 3).join(", ") || "—"}
                          {agentList.length > 3 && (
                            <span className="text-slate-400"> +{agentList.length - 3}</span>
                          )}
                        </div>
                        {agentList.length > 1 && (
                          <div className="text-[10px] text-slate-500">{agentList.length} different agents</div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="text-slate-700">
                          {campaignList.slice(0, 2).join(", ") || "—"}
                          {campaignList.length > 2 && (
                            <span className="text-slate-400"> +{campaignList.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top whitespace-nowrap text-slate-600">
                        <div>{fmtDate(g.first_attempt)}</div>
                        {g.attempts > 1 && (
                          <div className="text-[10px] text-slate-400">→ {fmtDate(g.last_attempt)}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${
                          CREDIT_BADGE_STYLES[g.best_credit_status] || CREDIT_BADGE_STYLES.no
                        }`}>
                          {creditLabel(g.best_row || { credit_status: g.best_credit_status })}
                        </span>
                      </td>
                      {canSeeMoney && (
                        <td className="px-3 py-2 align-top text-right font-mono">
                          {fmtAmt(g.deposit_sum_after)}
                          {g.deposit_count_after > 0 && (
                            <div className="text-[10px] text-slate-500">
                              {g.deposit_count_after} deposit{g.deposit_count_after === 1 ? "" : "s"}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-[11px] text-slate-500" data-testid="converted-users-footer">
            {viewMode === "users" ? (
              <>
                Showing {groupedRows.length.toLocaleString()} unique user{groupedRows.length === 1 ? "" : "s"} from {rows.length.toLocaleString()} credited row{rows.length === 1 ? "" : "s"}
                {truncated && ` (of ${totalCount.toLocaleString()} total)`}
                {truncated && " · export CSV for the full list"}
              </>
            ) : (
              <>
                Showing {rows.length.toLocaleString()} of {totalCount.toLocaleString()}
                {truncated && " · export CSV for the full list"}
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * Convenience button styled as an inline numeric cell that opens the drill-down.
 * Use anywhere you'd render `<td>{count}</td>` on a converted-count metric.
 */
export function ConvertedCountButton({ value, kind, className = "", onOpen, testId }) {
  const num = Number(value || 0);
  const hasValue = num > 0;
  return (
    <button
      type="button"
      onClick={() => hasValue && onOpen?.(kind)}
      disabled={!hasValue}
      className={`${className} ${
        hasValue
          ? "hover:underline hover:text-[#0A84FF] cursor-pointer"
          : "cursor-default"
      }`}
      data-testid={testId}
      title={hasValue ? `Click to see which users were credited (${KIND_LABELS[kind] || kind})` : "No conversions yet"}
    >
      {num.toLocaleString()}
    </button>
  );
}
